import React from 'react';

import {
  IconActivityHeartbeat,
  IconCalendarMonth,
  IconChartAreaLine,
  IconFileDollar,
  IconFileSearch,
  IconHierarchy,
  IconPrinter,
  IconReceipt,
  IconRipple,
} from '@tabler/icons-react';
import _ from 'lodash';

import {
  ADMIN_INVOICES_HREF,
  ADMIN_PAYROLL_HREF,
  ADMIN_PRINTING_HREF,
  ADMIN_TIMESHEET_HREF,
  ANALYTICS_HREF,
  APPS_HREF,
  NILE_HREF,
  NILE_SOURCES_HREF,
  NILE_TAXONOMY_HREF,
  STUDENT_ACTIVITY_LOG_HREF,
} from '../../utils/common/hrefs';
import { hasOverlap } from './utils';

import { UserGroup } from '@repo/validation';

// One entry in the Apps gallery: a page reached often enough to need a home,
// but not often enough to hold a place in the sidebar.
export interface AppOption {
  icon: React.ReactElement;
  name: string;
  href: string;
  // Endpoints the app owns, so the sidebar's Apps entry stays selected while
  // the reader is inside one.
  linkedEndpoints: string[];
  groups: UserGroup[];
}

const APPS: AppOption[] = [
  {
    icon: <IconFileDollar />,
    name: 'Payroll',
    href: ADMIN_PAYROLL_HREF,
    linkedEndpoints: ['admin/payroll'],
    groups: [UserGroup.Managers],
  },
  {
    icon: <IconCalendarMonth />,
    name: 'Timesheet',
    href: ADMIN_TIMESHEET_HREF,
    linkedEndpoints: ['admin/timesheet'],
    groups: [UserGroup.Managers],
  },
  {
    icon: <IconReceipt />,
    name: 'Invoices',
    href: ADMIN_INVOICES_HREF,
    linkedEndpoints: ['admin/invoices'],
    groups: [UserGroup.Admins],
  },
  {
    icon: <IconPrinter />,
    name: 'Printing',
    href: ADMIN_PRINTING_HREF,
    linkedEndpoints: ['admin/printing'],
    groups: [UserGroup.Admins],
  },
  {
    icon: <IconActivityHeartbeat />,
    name: 'Activity log',
    href: STUDENT_ACTIVITY_LOG_HREF,
    linkedEndpoints: ['student-activity-log'],
    groups: [UserGroup.Admins, UserGroup.Teachers],
  },
  {
    icon: <IconChartAreaLine />,
    name: 'Student analytics',
    href: ANALYTICS_HREF,
    linkedEndpoints: ['analytics'],
    groups: [UserGroup.Admins, UserGroup.Teachers],
  },
  // Nile is the codename for the quiz platform, which has no home of its own
  {
    icon: <IconRipple />,
    name: 'Nile',
    href: NILE_HREF,
    linkedEndpoints: ['nile'],
    groups: [UserGroup.Owners],
  },
  {
    icon: <IconFileSearch />,
    name: 'Nile (Sources)',
    href: NILE_SOURCES_HREF,
    linkedEndpoints: ['nile-sources'],
    groups: [UserGroup.Owners],
  },
  {
    icon: <IconHierarchy />,
    name: 'Nile (Taxonomy)',
    href: NILE_TAXONOMY_HREF,
    linkedEndpoints: ['nile-taxonomy'],
    groups: [UserGroup.Owners],
  },
];

// Every group with an app behind it, which the gallery's route is gated on.
export const APP_GROUPS: UserGroup[] = _.uniq(APPS.flatMap(app => app.groups));

// The apps a caller may open, in gallery order.
export const getAppOptions = (userGroups: string[]): AppOption[] =>
  APPS.filter(app => hasOverlap(userGroups, app.groups));

// Every endpoint an app owns, so the sidebar's Apps entry highlights on all of
// them rather than only on the gallery itself.
export const getAppEndpoints = (): string[] => [
  APPS_HREF.slice(1),
  ...APPS.flatMap(app => app.linkedEndpoints),
];
